import { create, StateCreator } from "zustand";
import { createJSONStorage, persist, PersistOptions } from 'zustand/middleware';
import { ICart, ICompleteList, IDetail, IMenuHour, IOrderInterval, IOrderList, IOrderModifiers } from '../types/menu';
import { useInfoStore } from "./userInfo";
import { orderLog, devLog, logger } from '../utils/logger';
var _ = require('lodash');


export interface IAyceMenu {
  id: number,
  isAyce: boolean,
  menuDesc: string,
  menuName: string,
  ordering: number,
  useHour: boolean,
  visible?: boolean,
  menuHours: IMenuHour[],
  ayceMainItem: IAyceItem,
  ayceSubItem: IAyceItem
}

export interface IAyceItem {
  id: number,
  itemDesc: string,
  itemName: string,
  itemPrice: number
}

type OrderStore = {
  openOrderList: boolean;
  openServerCall: boolean;
  openLanguageMenu: boolean;
  openCart: boolean;
  openCollect: boolean;
  isItemMaxAllowed: boolean;
  isItemMinRequired: boolean;
  isCategoryMaxAllowed: boolean;
  selectedCategoryCnt: number|null;
  updateCart: boolean;
  limitValue: number;
  categoryInCart: IOrderInterval[];
  cart: ICart[];
  preOrder: ICart | null;
  preRecommendOrder: ICart | null;
  totalPrice: number;
  totalCount: number;
  totalModifierPrice: number;
  orderPrice: number;
  orderCount: number;
  orderTotalPrice: number;
  orderList: IOrderList;
  openComplete: boolean;
  completeList: ICompleteList;
  collectInfo: Record<string, any>;
  snackMode: number;
  showSnackbar: boolean;
  totalGuest: number;
  selectedAyce : IAyceMenu|null,
  setOpenLanguageMenu: (value: boolean) => void;
  setOpenOrderList: (value: boolean) => void;
  setOpenServerCall: (value: boolean) => void;
  setOpenCart: (value: boolean) => void;
  setOpenCollect: (value: boolean) => void;
  setCart: (value: { item: ICart, type: string }) => void;
  setPreOrder: (value: ICart | null) => void;
  setPreRecommendOrder: (value: ICart | null) => void;
  checkedModifierPrice: (value: { price: number, type: string }) => void;
  calcOrderTotalPrice: (orderPrice: number) => void;
  setOrderPrice: (value: number) => void;
  setOrderCount: (value: number) => void;
  deleteCart: (item: ICart) => void;
  setOrderList: (value: IOrderList) => void;
  setOpenComplete: (value: boolean) => void;
  setCompleteList: (value: ICompleteList) => void;
  setCollectInfo: (value: JSON) => void;
  setSnackMode: (value: number) => void;
  setShowSnackbar: (value: boolean) => void;
  setIsItemMaxAllowed: (value: boolean) => void;
  setIsCategoryMaxAllowed: (value: boolean) => void;
  getSelectedCategoryCnt: (value: IDetail) => void;
  setUpdateCart: (value: boolean) => void;
  setLimitValue: (value: number) => void;
  resetOrder: () => void;
  resetOrderCart: () => void;
  setTotalGuest: (value: number) => void;
  setSelectedAyce : (value: IAyceMenu|null) => void;
  setCheckNewCart : (value: ICart[]) => void;
  clearCart: () => void;
}

type MyPersist = (
  config: StateCreator<OrderStore>,
  options: PersistOptions<OrderStore>
) => StateCreator<OrderStore>

type State = {
  openOrderList: boolean;
  openServerCall: boolean;
  openLanguageMenu: boolean;
  openCart: boolean;
  openCollect: boolean;
  openComplete: boolean;
  isItemMaxAllowed: boolean;
  isItemMinRequired: boolean;
  isCategoryMaxAllowed: boolean,
  selectedCategoryCnt: number|null,
  updateCart: boolean,
  limitValue: number,
  categoryInCart: IOrderInterval[],
  collectInfo: JSON;
  cart: ICart[];
  preOrder: ICart | null;
  preRecommendOrder: ICart | null;
  totalPrice: number;
  totalCount: number;
  totalModifierPrice: number;
  orderPrice: number;
  orderCount: number;
  orderTotalPrice: number;
  orderList: IOrderList;
  snackMode: number;
  showSnackbar: boolean;
  selectedAyce : IAyceMenu|null,
}

const initialState: State = {
  openOrderList: false,
  openServerCall: false,
  openLanguageMenu: false,
  openCart: false,
  openCollect: false,
  openComplete: false,
  isItemMaxAllowed: false,
  isItemMinRequired: false,
  isCategoryMaxAllowed: false,
  selectedCategoryCnt: 9999,
  updateCart: false,
  limitValue: 0,
  categoryInCart: [] as IOrderInterval[],
  collectInfo: {} as JSON,
  cart: [] as ICart[],
  preOrder: null,
  preRecommendOrder: null,
  totalPrice: 0,
  totalCount: 0,
  totalModifierPrice: 0,
  orderPrice: 0,
  orderCount: 1,
  orderTotalPrice: 0,
  orderList: {} as IOrderList,
  snackMode: 0,
  showSnackbar: false,
  selectedAyce: null
}

export const useOrderStore = create<OrderStore>(
    (persist as MyPersist)(
      (set, get): OrderStore =>  ({
       ...initialState,
      totalGuest: 1,
      openComplete: false,
      completeList: {} as ICompleteList,
      setTotalGuest: (value: number) => set({totalGuest: value}),
      setOpenLanguageMenu: (value: boolean) => set({openLanguageMenu: value}),
      setOpenOrderList: (value: boolean) => set({openOrderList: value}),
      setOpenServerCall: (value: boolean) => set({openServerCall: value}),
      setOpenCart: (value: boolean) => set({openCart: value}),
      setIsItemMaxAllowed: (value:boolean) => set({isItemMaxAllowed: value}),
      setIsCategoryMaxAllowed: (value:boolean) => set({isCategoryMaxAllowed: value}),
      setUpdateCart: (value:boolean) => set({updateCart: value}),
      setLimitValue: (value:number) => set({limitValue: value}),
      getSelectedCategoryCnt: (newItem: IDetail) => {
        set((state) => {
          if (newItem.categoryLimits?.maxAllowed === undefined) {
            logger.error("Max allowed category limit is undefined");
            return { selectedCategoryCnt: null };
          }

          const selectedCategoryItem = state.cart
            .filter((item) => item.categoryId === newItem.categoryId)
            .reduce((acc, curr) => acc + curr.quantity, 0);

          const selectedCategoryCnt = newItem.categoryLimits.maxAllowed ? newItem.categoryLimits.maxAllowed - selectedCategoryItem : 9999;
          orderLog("ableSelect", selectedCategoryCnt);
          return { selectedCategoryCnt };
        });
      },
      setCart: (value: { item: ICart, type: string }) => {
        return new Promise<void>((resolve, reject) => {
          try {
            set((state) => {
              const isAyce = useInfoStore.getState().isAyce;
              const newItem = value.item;
              logger.log(state);
      
              let copyArray = [...state.cart];
              let modifierPrice: number = 0;
              let selectedItemPerTable = 0; 
              let selectedCategoryItem = 0;

              state.isItemMaxAllowed = false;
              state.isItemMinRequired = false;
              state.isCategoryMaxAllowed = false;
              state.updateCart = false;
                          
              const findIndex = state.cart.findIndex(element => {
                const e1 = Object.entries(element).sort().filter(([key]) => !["orderItemPrice", "quantity", "subtotalAmount", "modifiersPrice", "modifierPrice", "itemWithModifierPrice", "newItem"].includes(key));
                const e2 = Object.entries(newItem).sort().filter(([key]) => !["orderItemPrice", "quantity", "subtotalAmount", "modifiersPrice", "modifierPrice", "itemWithModifierPrice", "newItem"].includes(key));
                return _.isEqual(JSON.stringify(e1), JSON.stringify(e2));
              });

              if (isAyce && newItem.maxAllowedPerTable) {
                const filteredItems = copyArray.filter(item => item.itemId === newItem.itemId);
                selectedItemPerTable = filteredItems.reduce((total, currentItem) => {
                  return total + (currentItem.quantity || 0);
                }, 0);

                logger.log('cart selectedItemPerTable', selectedItemPerTable);
              }

              // AYCE 조건에서 동일 카테고리 아이템 갯수 계산
              if (newItem.categoryLimits?.maxAllowed) {
                const filteredCategoryItems = copyArray.filter(
                  (item) => item.categoryId === newItem.categoryId
                );
                selectedCategoryItem = filteredCategoryItems.reduce(
                  (total, currentItem) => total + (currentItem.quantity || 0),
                  0
                );

                logger.log("AYCE selectedCategoryItem:", selectedCategoryItem);
              }


              if (newItem.hasModifier) {
                modifierPrice = calcOrderItemPrice(newItem.orderModifiers);
              }
              let itemWithModifierPrice: number = modifierPrice !== 0 ? (modifierPrice + newItem.itemPrice) : newItem.itemPrice;
      
      
             // 카트에 아이템 추가/수정 로직
              if (findIndex !== -1) {
                // 기존 아이템 수정
                if (value.type === "add") {
                  // AYCE 조건 체크
                  if(isAyce) {
                    if(newItem.maxAllowedPerTable === 0) {
                      return {
                      ...state,
                        snackMode: 12,
                        updateCart: false,
                        showSnackbar: true,
                        isItemMaxAllowed: true,
                        limitValue: newItem.maxAllowedPerTable
                      };
                    } else if (newItem.maxAllowedPerTable &&
                        selectedItemPerTable + newItem.quantity >
                          newItem.maxAllowedPerTable){
                      return {
                        ...state,
                        snackMode: 12,
                        updateCart: false,
                        showSnackbar: true,
                        isItemMaxAllowed: true,
                        limitValue:
                          newItem.maxAllowedPerTable -
                          selectedItemPerTable
                      };
                    } 
                  }
                  if (newItem.categoryLimits?.maxAllowed &&
                    selectedCategoryItem + newItem.quantity >
                      newItem.categoryLimits.maxAllowed)
                    {
                      return {
                        ...state,
                        snackMode: 10,
                        updateCart: false,
                        showSnackbar: true,
                        isItemMaxAllowed: true,
                        limitValue:
                          newItem.categoryLimits.maxAllowed -
                          selectedCategoryItem, // 최대 카테고리 제한
                      };
                    }
                  // 수량 추가
                  copyArray[findIndex].quantity += newItem.quantity;
                  state.updateCart = true;
                } else {
                  // 수량 감소
                  copyArray[findIndex].quantity -= newItem.quantity;
                  state.updateCart = false;
                  if (copyArray[findIndex].quantity <= 0) {
                    copyArray.splice(findIndex, 1); // 수량이 0 이하이면 카트에서 제거
                  }
                }
                let subtotalAmount = itemWithModifierPrice * copyArray[findIndex].quantity;
                copyArray[findIndex].subtotalAmount = subtotalAmount;
              } else {
                // 새로운 아이템 추가
                // if (isAyce) {
                  if(newItem.maxAllowedPerTable === 0) {
                    return {
                    ...state,
                      snackMode: 12,
                      updateCart: false,
                      showSnackbar: true,
                      isItemMaxAllowed: true,
                      limitValue: newItem.maxAllowedPerTable
                    };
                  } else if(newItem.categoryLimits?.maxAllowed &&
                  selectedCategoryItem + newItem.quantity >
                    newItem.categoryLimits.maxAllowed) {
                      return {
                        ...state,
                        snackMode: 10,
                        updateCart: false,
                        showSnackbar: true,
                        isItemMaxAllowed: true,
                        limitValue:
                          newItem.categoryLimits.maxAllowed - selectedCategoryItem, // 최대 카테고리 제한
                      };
                  } else if((newItem.maxAllowedPerTable &&
                    selectedItemPerTable + newItem.quantity >
                      newItem.maxAllowedPerTable))
                  {
                      return {
                        ...state,
                        snackMode: 12,
                        updateCart: false,
                        showSnackbar: true,
                        isItemMaxAllowed: true,
                        limitValue:
                          newItem.maxAllowedPerTable === -1 ? 0: newItem.maxAllowedPerTable -
                          selectedItemPerTable 
                      };
                  }
              // }

                let subtotalAmount = itemWithModifierPrice * newItem.quantity;
                copyArray.push({
                  ...newItem,
                  modifierPrice,
                  itemWithModifierPrice,
                  subtotalAmount,
                });
              }

              // 카트 업데이트
              state.cart = copyArray;
              updateTotalPrice(state); // 총 금액 업데이트
              if (newItem.categoryLimits?.orderIntervalMinutes) {
                logger.log("카테고리 타이머 정보 추가:", {
                  categoryId: newItem.categoryId,
                  orderIntervalMinutes: newItem.categoryLimits.orderIntervalMinutes
                });
                let set = new Set(state.categoryInCart.map(obj => JSON.stringify(obj)));
                set.add(JSON.stringify({ categoryId: newItem.categoryId, orderIntervalMinutes: newItem.categoryLimits.orderIntervalMinutes }));
                state.categoryInCart = Array.from(set).map(str => JSON.parse(str));
                logger.log("현재 등록된 카테고리 타이머 정보:", state.categoryInCart);
              }
              state.updateCart = true;
              resolve();
              return { ...state, cart: state.cart };
            });
          } catch (error) {
            reject(error);
          }
        });
      },      
      setPreOrder: (order: ICart | null) => {
        return new Promise<void>((resolve, reject) => {
          try {
            set((state) => {
              logger.log(state);

              if (!order) {
                return { ...state, preOrder: null };
              }

              let modifierPrice: number = 0;
      
              if (order.hasModifier) {
                modifierPrice = calcOrderItemPrice(order.orderModifiers);
              }
              let itemWithModifierPrice: number = modifierPrice !== 0 ? (modifierPrice + (order.itemPrice || 0)) : (order.itemPrice || 0);
              let subtotalAmount = itemWithModifierPrice;
              return { ...state, preOrder: {...order, modifierPrice, itemWithModifierPrice, subtotalAmount } };
            });
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      },      
      calcOrderTotalPrice : (modifierPrice) => { set((state) => {
          let totalPrice = 0;
          totalPrice = (state.orderPrice + modifierPrice) * state.orderCount;
          // logger.log(totalPrice, state.orderPrice, state.orderCount)
          return {orderTotalPrice: totalPrice}
        })
      },
      checkedModifierPrice: (value: {price: number, type: string}) => { set((state) => {
        let totalPrice = 0
        // logger.log(value.price, value.type)
        if(value.type === "add") {
          totalPrice = state.totalModifierPrice + value.price
          // logger.log('add', totalPrice, value.price)
        } else if(value.type === "sub") {
          totalPrice = state.totalModifierPrice - value.price
          // logger.log('sub', totalPrice, value.price)
        } else {
          totalPrice = value.price
        }
        state.calcOrderTotalPrice(totalPrice)
        return { totalModifierPrice: totalPrice} 
        })
      },
      setOrderPrice: (value: number) => {set((state) => {
        state.orderPrice = value;
        state.calcOrderTotalPrice(state.totalModifierPrice)
        return {orderPrice: value}
        })
      },
      setOrderCount: (value: number) => {set((state) => {
        state.orderCount = value;
        state.calcOrderTotalPrice(state.totalModifierPrice)
         return {orderCount: value}
        })
      },
      deleteCart: (item: ICart) => { set((state) =>{
          const findIndex = state.cart.findIndex((element) => {
            const e1 = Object.entries(element).sort().filter(([key]) => !["orderItemPrice", "quantity", "subtotalAmount"].includes(key));
            const e2 = Object.entries(item).sort().filter(([key]) => !["orderItemPrice", "quantity", "subtotalAmount"].includes(key));
            return _.isEqual(JSON.stringify(e1),JSON.stringify(e2));
          });
          logger.log("deleteCart - 제거할 아이템 찾음:", findIndex, item);
          let copyArray = [...state.cart];
          if(findIndex !== -1) {
            delete copyArray[findIndex]
            state.cart = copyArray.filter(function( element ) {
              return element !== undefined;
            });
          } 
          
          logger.log("deleteCart - 카테고리 제약 정보 업데이트 전:", state.categoryInCart);
          
          // 카테고리 제약 정보 업데이트
          const categoryExists = state.cart.some(cartItem => cartItem.categoryId === item.categoryId);
          if (!categoryExists) {
            logger.log("deleteCart - 해당 카테고리 아이템이 모두 제거됨:", item.categoryId);
            
            let set = new Set(state.categoryInCart.map(obj => JSON.stringify(obj)));
            set.delete(JSON.stringify({categoryId : item.categoryId, orderIntervalMinutes : item.categoryLimits?.orderIntervalMinutes}));
            state.categoryInCart = Array.from(set).map(str => JSON.parse(str));
            
            logger.log("deleteCart - 카테고리 제약 정보 업데이트 후:", state.categoryInCart);
          } else {
            logger.log("deleteCart - 해당 카테고리의 다른 아이템이 여전히 존재함:", item.categoryId);
          }
          
          updateTotalPrice(state)
          return { cart: state.cart}
        })
      },
      setOrderList: (item: IOrderList) => set({orderList: item}),
      setOpenComplete: (value: boolean) => set({openComplete: value}),
      setCompleteList: (items: ICompleteList) =>  set({completeList: items}),
      setOpenCollect: (value:boolean) => set({openCollect: value}),
      setCollectInfo: (value: JSON) => set({collectInfo: value}),
      setSnackMode: (value:number) => set({snackMode: value}),
      setShowSnackbar:  (value:boolean) => set({showSnackbar: value}),
      setSelectedAyce:  (value:IAyceMenu|null) => set({selectedAyce: value}),
      setCheckNewCart: (item: ICart[]) => {
        const updatedCart = get().cart.map(item =>
          item.newItem ? { ...item, newItem: false } : item
        )
        set({ cart: updatedCart})
      },
      resetOrder: () => set({orderPrice : 0, orderCount: 1, totalModifierPrice: 0, orderTotalPrice: 0 }),
      resetOrderCart: () => {
        if (window.Android) {
          window.Android.postMessage(JSON.stringify({
            type: 'cart',
            count: 0
          }));
        }
        set((state) => {
          logger.log(initialState)
          return {...initialState, cart: []}
        })
      },
      setPreRecommendOrder: (order: ICart | null) => {
        return new Promise<void>((resolve, reject) => {
          try {
            set((state) => {
              logger.log(state);

              if (!order) {
                return { ...state, preRecommendOrder: null };
              }

              let modifierPrice: number = 0;
      
              if (order.hasModifier) {
                modifierPrice = calcOrderItemPrice(order.orderModifiers);
              }
              let itemWithModifierPrice: number = modifierPrice !== 0 ? (modifierPrice + (order.itemPrice || 0)) : (order.itemPrice || 0);
              let subtotalAmount = itemWithModifierPrice;
              return { ...state, preRecommendOrder: {...order, modifierPrice, itemWithModifierPrice, subtotalAmount } };
            });
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      },
      clearCart: () => set({ cart: [] }),
    }), 
    {
      name: 'order',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
function updateTotalPrice(state: OrderStore) {
  state.totalCount = state.cart.reduce((count, item) => count + item.quantity, 0);
  state.totalPrice = state.cart.reduce((price, item) => price + item.quantity * item.itemWithModifierPrice!, 0);

  // View cart의 totalCount를 사용하여 안드로이드로 메시지 전송
  if (window.Android) {
    window.Android.postMessage(JSON.stringify({
      type: 'cart',
      count: state.totalCount
    }));
  }
}
function calcOrderItemPrice(modifierGroups: IOrderModifiers[]): number {
  return modifierGroups.reduce((total: number, modifier: IOrderModifiers) => {
    const currentModifierPrice = parseFloat(modifier.modifierPrice.toString()) * modifier.quantity;

    // 재귀적으로 하위 modifiers의 가격 합산
    const nestedModifiersPrice = modifier.orderModifiers
      ? calcOrderItemPrice(modifier.orderModifiers)
      : 0;

    return total + currentModifierPrice + nestedModifiersPrice;
  }, 0);
}

// function calcOrderCumulativeItemPrice(modifierGroups: IOrderModifiers[]): number {
//   return modifierGroups.reduce((total: number, modifier: IOrderModifiers) => {
//     const currentModifierPrice = parseFloat(modifier.modifierPrice.toString()) * modifier.cumulativeQuantity;

//     // 재귀적으로 하위 modifiers의 가격 합산
//     const nestedModifiersPrice = modifier.orderModifiers
//       ? calcOrderCumulativeItemPrice(modifier.orderModifiers)
//       : 0;

//     return total + currentModifierPrice + nestedModifiersPrice;
//   }, 0);
// }
// function checkingCategoryMaxLimits(state: OrderStore, newItem: ICart, type: string) {
//   if (!newItem.categoryLimits?.maxAllowed) return false;

//   const selectedCategoryItem = state.cart
//     .filter((item) => item.categoryId === newItem.categoryId)
//     .reduce((acc, curr) => acc + curr.quantity, 0);

//   state.limitValue = newItem.categoryLimits.maxAllowed;

//   if (type === "add" && selectedCategoryItem + newItem.quantity > newItem.categoryLimits.maxAllowed) return true;
//   return false;
// }


